import * as React from 'react';
import Chip from '@mui/material/Chip';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import type { MenuProps } from '@mui/material/Menu';
import Select, { SelectProps } from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import InfoIcon from '@mui/icons-material/Info';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import DoneIcon from '@mui/icons-material/Done';
import {
  GridEditModes,
  GridRenderCellParams,
  GridRenderEditCellParams,
  useGridApiContext,
  useGridRootProps,
} from '@mui/x-data-grid';



interface StatusProps {
  status: string;
}
export const STATUS_OPTIONS = ['Open', 'PartiallyFilled', 'Filled', 'Rejected'];



const StyledChip = styled(Chip)(({ theme }) => ({
    justifyContent: 'left',
    '& .icon': {
      color: 'inherit',
    },
    '&.Open': {
      color: theme.palette.info.dark,
      border: `1px solid ${theme.palette.info.main}`,
    },
    '&.Filled': {
      color: theme.palette.success.dark,
      border: `1px solid ${theme.palette.success.main}`,
    },
    '&.PartiallyFilled': {
      color: theme.palette.warning.dark,
      border: `1px solid ${theme.palette.warning.main}`,
    },
    '&.Rejected': {
      color: theme.palette.error.dark,
      border: `1px solid ${theme.palette.error.main}`,
    },
  }));

const Status = React.memo((props: StatusProps) => {
  const { status } = props;

  let icon: any = null;
  if (status === 'Rejected') {
    icon = <ReportProblemIcon className="icon" />;
  } else if (status === 'Open') {
    icon = <InfoIcon className="icon" />;
  } else if (status === 'PartiallyFilled') {
    icon = <AutorenewIcon className="icon" />;
  } else if (status === 'Filled') {
    icon = <DoneIcon className="icon" />;
  }

  let label: string = status;
  if (status === 'PartiallyFilled') {
    label = 'Μερικώς εκτελεσμένη';
  }
  else if (status === 'Open') {
    label = 'Προς Εκτέλεση';
  }
  else if (status === 'Filled') {
    label = 'Εκτελεσμένη';
  }
  return (
    <StyledChip
      className={status}
      icon={icon}
      size="small"
      label={label}
      variant="outlined"
    />
  );
});

function EditStatus(props: GridRenderEditCellParams<any, string>) {
  const { id, value, field } = props;
  const rootProps = useGridRootProps();
  const apiRef = useGridApiContext();

  const handleChange: SelectProps['onChange'] = async (event) => {
    const isValid = await apiRef.current.setEditCellValue({
      id,
      field,
      value: event.target.value,
    });

    if (isValid && rootProps.editMode === GridEditModes.Cell) {
      apiRef.current.stopCellEditMode({ id, field, cellToFocusAfter: 'below' });
    }
  };

  const handleClose: MenuProps['onClose'] = (event, reason) => {
    if (reason === 'backdropClick') {
      apiRef.current.stopCellEditMode({ id, field, ignoreModifications: true });
    }
  };

  return (
    <Select
      value={value}
      onChange={handleChange}
      MenuProps={{
        onClose: handleClose,
      }}
      sx={{
        height: '100%',
        '& .MuiSelect-select': {
          display: 'flex',
          alignItems: 'center',
          pl: 1,
        },
      }}
      autoFocus
      fullWidth
      open
    >
      {STATUS_OPTIONS.map((option) => {
        let IconComponent: any = null;
        if (option === 'Rejected') {
          IconComponent = ReportProblemIcon;
        } else if (option === 'Open') {
          IconComponent = InfoIcon;
        } else if (option === 'PartiallyFilled') {
          IconComponent = AutorenewIcon;
        } else if (option === 'Filled') {
          IconComponent = DoneIcon;
        }

        let label = option;
        if (option === 'PartiallyFilled') {
          label = 'Partially Filled';
        }

        return (
          <MenuItem key={option} value={option}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <IconComponent fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={label} sx={{ overflow: 'hidden' }} />
          </MenuItem>
        );
      })}
    </Select>
  );
}

export function renderStatus(params: GridRenderCellParams<any, string>) {
  if (params.value == null) {
    return '';
  }

  return <Status status={params.value} />;
}

export function renderEditStatus(params: GridRenderEditCellParams<any, string>) {
  return <EditStatus {...params} />;
}

//export const CLAIM_STATUS = ['Αδιάφоρο', 'Υποβλήθηκε', 'Ελέγχθηκε', 'Εγκρίθηκε','Ακυρώθηκε','Ολοκληρώθηκε','Εκκρεμεί'];
export const CLAIM_STATUS_OPTIONS = ['none', 'submitted', 'checked', 'approved','canceled','completed','pending'];
/*
 100	ΑΔΙΑΦΟΡΟ
101	ΥΠΟΒΛΗΘΗΚΕ
102	ΕΛΕΓΧΘΗΚΕ
103	ΕΓΚΡΙΘΗΚΕ
104	ΑΚΥΡΩΘΗΚΕ
105	ΟΛΟΚΛΗΡΩΘΗΚΕ
111	ΕΚΚΡΕΜΟΤΗΤΕΣ 

palette.primary.light #42a5f5
palette.primary.main #1976d2
palette.primary.dark #1565c0

palette.secondary.light #ba68c8
palette.secondary.main #9c27b0
palette.secondary.dark #7b1fa2

palette.error.light #ef5350
palette.error.main #d32f2f

palette.warning.light #ff98
palette.warning.main #ed6c02

palette.info.light #03a9f4
palette.info.main #0288d1
palette.info.dark #01579b

palette.success.light #4caf50

*/
const ClaimStyledChip = styled(Chip)(({ theme }) => ({
    justifyContent: 'left',
    '& .icon': {
      color: 'inherit',
    },
    '&.none': {
      color: theme.palette.secondary.dark,
      border: `1px solid ${theme.palette.info.main}`,
    },
    '&.submitted': {
      color: theme.palette.info.dark,
      border: `1px solid ${theme.palette.info.main}`,
    },
    '&.checked': {
      color: theme.palette.info.dark,
      border: `1px solid ${theme.palette.info.main}`,
    },        
    '&.approved': {
      color: theme.palette.success.dark,
      border: `1px solid ${theme.palette.success.main}`,
    },
    '&.completed': {
      color: theme.palette.success.dark,
      border: `1px solid ${theme.palette.success.main}`,
    },    
    '&.pending': {
      color: theme.palette.warning.dark,
      border: `1px solid ${theme.palette.warning.main}`,
    },
    '&.canceled': {
      color: theme.palette.error.dark,
      border: `1px solid ${theme.palette.error.main}`,
    },
  }));

  const ClaimStatus = React.memo((props: StatusProps) => {
    const { status } = props;

    let icon: any = null;
    if (status === 'canceled') {
      icon = <ReportProblemIcon className="icon" />;
    } else if (status === 'submitted' || status === 'checked') {
      icon = <InfoIcon className="icon" />;
    } else if (status === 'pending') {
      icon = <AutorenewIcon className="icon" />;
    } else if (status === 'approved' || status === 'completed') {
      icon = <DoneIcon className="icon" />;
    }
    //export const CLAIM_STATUS = ['Αδιάφоρο', 'Υποβλήθηκε', 'Ελέγχθηκε', 'Εγκρίθηκε','Ακυρώθηκε','Ολοκληρώθηκε','Εκκρεμεί'];
    // export const CLAIM_STATUS_OPTIONS = ['none', 'submitted', 'checked', 'approved','canceled','completed','pending'];
    let label: string = status;
    if (status === 'pending') {
      label = 'Εκκρεμότητες';
    }
    else if (status === 'submitted') {
      label = 'Προς Έλεγχο';
    }
    else if (status === 'checked') {
      label = 'Ελέγχθηκε';
    }
    else if (status === 'approved') {
      label = 'Εγκρίθηκε';
    }
    else if (status === 'canceled') {
      label = 'Ακυρώθηκε';
    }
    else if (status === 'completed') {
      label = 'Ολοκληρώθηκε';
    }
    else label = 'none';
    
    return (
      <ClaimStyledChip
        className={status}
        icon={icon}
        size="small"
        label={label}
        variant="outlined"
      />
    );
});

export function claimRenderStatus(params: GridRenderCellParams<any, string>) {
  if (params.value == null) {
    return '';
  }

  return <ClaimStatus status={params.value} />;
}
